"use client";

import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type TransitionEvent,
} from "react";
import { renderMarkdown } from "@/lib/markdown";
import { cn } from "@/lib/utils";
import { ChevronDownIcon } from "@/components/icons/ChevronDownIcon";

/**
 * The history frame's "hold your place": a fold decided under a row changes
 * the height, so the frame puts the reader back (pinned to the newest, or the
 * same distance from the bottom when the row sits above them). It is handed
 * the row's element. PillHistory provides it.
 */
export const HistoryRelayout = createContext<(changed?: HTMLElement) => void>(
  () => undefined
);

/** The fold shows this many lines (max-h-[9.6em] below, at line-height 1.6). */
const SHOWN_LINES = 6;
/** Folds only past a full extra line beyond what's shown, so "Show more"
 *  never appears for a reply the fold would already show all of. */
const FOLD_OVER_LINES = SHOWN_LINES + 1;
/** 14 px at 1.6: the line when the browser reports none (jsdom). */
const FALLBACK_LINE_PX = 22.4;
/** Air above the turn when Show less brings a scrolled-away fold back. */
const BACK_INTO_VIEW_PX = 12;

/**
 * A reply as markdown (the product's renderMarkdown). Taller than a full line
 * past what the fold shows (6 lines) folds behind "Show more" / "Show less",
 * so the affordance never appears unless there is truly at least one more
 * line to reveal. The fold is decided in a layout effect (and re-decided on
 * resize — the popover's own width can still be settling), before paint, so
 * nothing jumps; opening and closing animate the height only.
 */
export function FoldedAnswer({ markdown }: { markdown: string }) {
  const html = useMemo(() => renderMarkdown(markdown), [markdown]);
  const ref = useRef<HTMLDivElement>(null);
  const relayout = useContext(HistoryRelayout);
  const [foldable, setFoldable] = useState(false);
  const [open, setOpen] = useState(false);

  // A ResizeObserver, not a one-time measurement on [html]: PillHistory now
  // floats in a Radix popover whose own width (--radix-popover-trigger-width)
  // lands in a parent's layout effect, which runs AFTER this one (children
  // fire first) — measuring once here could catch the answer at a transient
  // pre-settle width, wrap it onto a wrong line count, and never correct it.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (): void => {
      const line =
        parseFloat(getComputedStyle(el).lineHeight) || FALLBACK_LINE_PX;
      setFoldable(el.scrollHeight > line * FOLD_OVER_LINES);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [html]);
  // The fold shrank this row after the frame placed the reader: place them again.
  useLayoutEffect(() => {
    if (foldable && ref.current) relayout(ref.current);
  }, [foldable, relayout]);

  // Opening runs the height from the fold to the words' own height, then lets
  // it go (so a narrower window never clips it); closing runs it back from
  // the height it has now. Both are measurements, so they are set on the node.
  const toggle = (): void => {
    const el = ref.current;
    if (!el) return;
    if (!open) {
      el.style.maxHeight = `${el.scrollHeight}px`;
      setOpen(true);
      return;
    }
    el.style.maxHeight = `${el.offsetHeight}px`;
    void el.offsetHeight; // the start height lands before the fold's own
    el.style.maxHeight = "";
    setOpen(false);
    // The fold's top stays where it is while it closes: bring it back when it
    // had scrolled above the log.
    const log = el.closest<HTMLElement>('[role="log"]');
    if (!log) return;
    const top =
      el.getBoundingClientRect().top - log.getBoundingClientRect().top;
    if (top < 0) log.scrollBy({ top: top - BACK_INTO_VIEW_PX });
  };
  const settle = (e: TransitionEvent<HTMLDivElement>): void => {
    if (e.propertyName === "max-height" && open && ref.current)
      ref.current.style.maxHeight = "none";
  };

  return (
    <>
      <div
        ref={ref}
        className={cn(
          "bh-answer w-full min-w-0 text-3.5 leading-[1.6] text-fg-default wrap-anywhere",
          foldable &&
            "max-h-[9.6em] overflow-hidden transition-[max-height] duration-300 ease-out",
          foldable &&
            !open &&
            "[mask-image:linear-gradient(to_bottom,#000_calc(100%-2.4em),transparent)]"
        )}
        data-testid="history-answer"
        data-folded={foldable && !open ? "" : undefined}
        onTransitionEnd={settle}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {foldable && (
        <button
          type="button"
          aria-expanded={open}
          onClick={toggle}
          className="group/more -mt-1 inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent px-0 py-0.5 text-3 font-medium text-gold-ink"
        >
          <span className="underline-offset-2 group-hover/more:underline">
            {open ? "Show less" : "Show more"}
          </span>
          <ChevronDownIcon
            aria-hidden="true"
            className={cn(
              "transition-bh-rotate size-3.5",
              open && "rotate-180"
            )}
          />
        </button>
      )}
    </>
  );
}
